import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './rednote-app-launcher.js';
import { useLightbox } from '../components/Lightbox';
import ScanImages from '../components/ScanImages';

const API_BASE = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_API_URL || 'http://localhost:4001')
  : '';

export default function ContentPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const channel = searchParams.get('channel') || 'xhs';

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');   // 页内提示（不覆盖整页）
  const [fatal, setFatal] = useState('');     // 加载失败，整页替换
  const [copied, setCopied] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  // 内容只作参考写法，复制/发布用的都是用户在这里改过的版本
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [touched, setTouched] = useState(false);
  const [images, setImages] = useState([]);
  const lb = useLightbox();

  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/api/scan/content/${id}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setArticle(data);
        setImages((data.images || []).filter(u => /^https?:\/\//i.test(u)));
      })
      .catch(() => setFatal('内容加载失败，请重新扫码'))
      .finally(() => setLoading(false));
  }, [id]);

  const exampleTitle   = channel === 'google' ? (article?.titleEn   || article?.title   || '') : (article?.title   || '');
  const exampleContent = channel === 'google' ? (article?.contentEn || article?.content || '') : (article?.content || '');
  const displayTitle   = touched ? title : exampleTitle;
  const displayContent = touched ? content : exampleContent;
  const editTitle = (v) => { setTouched(true); setTitle(v); };
  const editContent = (v) => { setTouched(true); setContent(v); };

  const handleCopy = async () => {
    const text = `${displayTitle}\n\n${displayContent}`.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePublishXhs = () => {
    if (!(displayContent || '').trim()) { setStatus('请先写下你自己的体验再发布'); return; }
    if (!images.filter((u) => /^https?:\/\//i.test(u)).length) { setStatus('请先加一张图片再发布'); return; }
    const launcher = window.RednoteAppLauncher;
    if (!launcher) { setStatus('页面加载异常，请刷新重试'); return; }
    const shareInfo = {
      type: 'normal',
      title: (displayTitle || '').trim(),
      content: (displayContent || '').trim(),
      images: images.filter((u) => /^https?:\/\//i.test(u)),
    };
    const result = launcher.openPublish(shareInfo);
    if (!result.ok) {
      if (result.reason === 'in_app_browser') {
        setStatus('请点击右上角菜单，在系统浏览器中打开后再发布');
      } else if (result.reason === 'unsupported') {
        setStatus('请在手机上打开此页面');
      }
    }
  };


  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.dots}>
          {[0,1,2].map(i => <span key={i} style={{ ...styles.dot, animationDelay: `${i * 0.18}s` }} />)}
        </div>
      </div>
    );
  }

  if (!article || fatal) {
    return <div style={styles.center}><p style={styles.errText}>{fatal || '内容不存在'}</p></div>;
  }

  if (channel === 'google') {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.googleBrand}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285f4"/>
            </svg>
            <span style={styles.googleBrandText}>Google Review</span>
          </div>
        </div>

        <div style={styles.body}>
          <div style={styles.imageSection}>
            <ScanImages
              articleId={id}
              images={images}
              onChange={setImages}
              onOpen={(list, i) => lb.open(list, i)}
              hint="示例图仅供参考，建议用你自己拍的照片；长按图片可保存到相册"
            />
          </div>

          <div style={styles.contentCard}>
            <div style={styles.exampleRow}>
              <span style={styles.exampleTag}>Example</span>
              <span style={styles.exampleHint}>
                This is only an example — please write your own honest experience before posting.
              </span>
            </div>
            <input
              style={styles.titleInput}
              value={displayTitle}
              onChange={(e) => editTitle(e.target.value)}
              placeholder="Title (optional)"
            />
            <textarea
              style={styles.contentInput}
              value={displayContent}
              onChange={(e) => editContent(e.target.value)}
              placeholder="Write your own experience..."
              rows={10}
            />
          </div>

          {status && <p style={styles.statusMsg}>{status}</p>}
        </div>

        <div style={styles.fixedBottom}>
          <button style={styles.copyBtn} onClick={handleCopy}>
            {copied ? '✓ Copied!' : 'Copy My Review'}
          </button>
          {article.googleReviewUrl && (
            <a href={article.googleReviewUrl} target="_blank" rel="noreferrer" style={styles.googleBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="white"/>
              </svg>
              Write Google Review
            </a>
          )}
        </div>

        {lb.node}
      </div>
    );
  }

  // XHS channel
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.xhsBrand}>
          <img src="/favicon.png" alt="" style={styles.brandLogo} />
          <span style={styles.xhsBrandText}>券宝宝</span>
        </div>
      </div>

      <div style={styles.body}>
        <div style={styles.imageSection}>
          {images.length > 0 && (
            <div style={styles.carousel}>
              <img
                src={images[Math.min(activeImg, images.length - 1)]}
                alt=""
                style={styles.carouselImg}
                onClick={() => lb.open(images, Math.min(activeImg, images.length - 1))}
              />
            </div>
          )}
          <ScanImages
            articleId={id}
            images={images}
            onChange={(next) => { setImages(next); setActiveImg((i) => Math.min(i, Math.max(0, next.length - 1))); }}
            onOpen={(list, i) => setActiveImg(i)}
            hint="上面的图也只是参考，建议换成你自己拍的照片"
          />
        </div>
        <div style={styles.contentCard}>
          <div style={styles.exampleRow}>
            <span style={styles.exampleTag}>参考写法</span>
            <span style={styles.exampleHint}>下面只是一个参考，建议你自己发挥，写成你自己的真实感受再发布</span>
          </div>
          <input
            style={styles.titleInput}
            value={displayTitle}
            onChange={(e) => editTitle(e.target.value)}
            placeholder="写个标题……"
          />
          <textarea
            style={styles.contentInput}
            value={displayContent}
            onChange={(e) => editContent(e.target.value)}
            placeholder="写下你自己的体验……"
            rows={10}
          />
        </div>
        <p style={styles.complianceTip}>如果你是参加活动来分享的，记得在文里提一句</p>

        {/* 扫码的人本身就在发小红书，顺手招募：这是最便宜也最合规的博主来源 */}
        <div style={styles.recruitBox} onClick={() => { window.location.hash = '/blogger/enroll'; }}>
          <div style={styles.recruitTitle}>想接商家的探店邀请？</div>
          <div style={styles.recruitSub}>免费吃喝 + 商家给套餐，助理会帮你约时间。点这里登记</div>
        </div>

        {status && <p style={styles.statusMsg}>{status}</p>}
      </div>

      <div style={styles.fixedBottom}>
        <button style={styles.xhsBtn} onClick={handlePublishXhs}>发布到小红书</button>
      </div>

      {lb.node}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column', paddingBottom: 80 },
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' },
  dots: { display: 'flex', gap: 6 },
  dot: {
    width: 8, height: 8, borderRadius: '50%', background: '#FA1C51',
    animation: 'bounce 0.9s ease-in-out infinite',
  },
  errText: { color: '#9ca3af', fontSize: 15 },

  header: {
    background: '#fff', padding: '14px 16px',
    borderBottom: '1px solid #f0f0f0',
  },
  googleBrand: { display: 'flex', alignItems: 'center', gap: 8 },
  googleBrandText: { fontSize: 15, fontWeight: 700, color: '#4285f4' },
  xhsBrand: { display: 'flex', alignItems: 'center', gap: 8 },
  brandLogo: { width: 24, height: 24, borderRadius: 6 },
  xhsBrandText: { fontSize: 15, fontWeight: 700, color: '#FA1C51' },

  body: { flex: 1, padding: '12px 12px 0' },
  imageSection: { marginBottom: 12 },
  thumbRow: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 },
  thumb: { width: 80, height: 80, objectFit: 'cover', borderRadius: 8, display: 'block', flexShrink: 0 },
  saveHint: { fontSize: 12, color: '#9ca3af', margin: '6px 0 0', textAlign: 'center' },

  contentCard: { background: '#fff', borderRadius: 12, padding: '14px 14px', marginBottom: 12 },
  title: { fontSize: 17, fontWeight: 700, color: '#111', margin: '0 0 10px', lineHeight: 1.4 },
  content: { fontSize: 15, color: '#374151', lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' },
  exampleRow: { marginBottom: 10 },
  exampleTag: {
    display: 'inline-block', fontSize: 11, fontWeight: 600, color: '#FA1C51',
    background: '#fff1f4', borderRadius: 4, padding: '2px 7px', marginRight: 6,
  },
  exampleHint: { fontSize: 12, color: '#888', lineHeight: 1.6 },
  titleInput: {
    width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
    fontSize: 17, fontWeight: 700, color: '#111', lineHeight: 1.4,
    padding: 0, margin: '0 0 10px', background: 'transparent', fontFamily: 'inherit',
  },
  contentInput: {
    width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
    fontSize: 15, color: '#374151', lineHeight: 1.75, padding: 0,
    background: 'transparent', resize: 'vertical', fontFamily: 'inherit',
  },
  recruitBox: {
    background: '#fff', border: '1px solid #ffe4e9', borderRadius: 10,
    margin: '0 12px 12px', padding: '12px 14px', cursor: 'pointer',
  },
  recruitTitle: { fontSize: 14, fontWeight: 600, color: '#FA1C51', marginBottom: 3 },
  recruitSub: { fontSize: 12, color: '#9ca3af', lineHeight: 1.6 },
  complianceTip: { fontSize: 12, color: '#9ca3af', margin: '0 0 12px', lineHeight: 1.6 },
  statusMsg: { textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '8px 0' },

  fixedBottom: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: '#fff', borderTop: '1px solid #f0f0f0',
    padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8,
  },
  copyBtn: {
    width: '100%', padding: '13px', border: '1.5px solid #4285f4',
    borderRadius: 10, background: '#fff', color: '#4285f4',
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  googleBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    width: '100%', padding: '13px', border: 'none',
    borderRadius: 10, background: '#4285f4', color: '#fff',
    fontSize: 15, fontWeight: 700, textDecoration: 'none',
    boxSizing: 'border-box',
  },
  xhsBtn: {
    width: '100%', padding: '14px', border: 'none',
    borderRadius: 10, background: '#FA1C51', color: '#fff',
    fontSize: 16, fontWeight: 700, cursor: 'pointer',
  },
};
